import React from "react";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import Container from "@material-ui/core/Container";
import Divider from "@material-ui/core/Divider";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import BrokenImageIcon from '@material-ui/icons/BrokenImage';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';

import {makeStyles} from "@material-ui/core/styles";
import {Link as RouterLink} from "react-router-dom";

import {Brand} from "../Brand";
import {ChatData, ConfSubscriber, stageClient} from "../logic/StageClient";
import {ChatIconAvatar} from "../components/ChatIconAvatar";
import {DailyFeed} from "../components/DailyFeed";
import {LocalStorageKeys} from "../logic/Persistence";
import {Messaging} from "../Messaging";
import {Post} from "../components/Post";
import {SpeakerAvatar} from "../components/SpeakerAvatar";


const useLocalStyles = makeStyles(theme => ({
  centerVertical: {
    marginTop: 'auto',
    marginBottom: 'auto',
  },
  infoCard: {
    margin: '24px auto',
    padding: '8px',
  },
  chatListPaper: {
    padding: theme.spacing(1),
    borderRadius: 12,
    boxShadow: '0 2px 20px 0px rgba(0, 0, 0, 0.05), 0 7px 12px -5px rgba(233, 30, 99, 0.46)',
    marginTop: '38px',
    marginBottom: '38px',
  },
  chatListItems: {
    minHeight: '5em',
  },
  speakerAvatarContainer: {
    paddingTop: '2px',
    paddingBottom: 0,
  },
  speakerAvatarMicro: {
    height: '20px',
    width: '20px',
    marginRight: '0px',
    filter: 'drop-shadow(1px 1px 0 #AAA)',
  },
  dailyPaper: {
    boxShadow: Brand.styles.appbar_blue.boxShadow,
    padding: theme.spacing(1),
    borderRadius: 12,
    marginTop: '38px',
    marginBottom: '38px',
  },
}));

/**
 * A list item that shortly summarizes the chat.
 */
function ChatListItem({chatData, classes}: { chatData: ChatData, classes: any }) {
  const chatId = chatData.chatId;
  // @ts-ignore: this is here to "force a cast" since the 'chatSnapshot' is not null
  const chatSnapshot: StageTypes.ChatSnapshot = chatData.chatSnapshot;
  const chatWatchers = chatSnapshot.stage.watchers;

  const MAX_SPEAKER_AVATARS = 6;
  const chatSpeakerIds = chatSnapshot.userIds;
  const availableSpeakers = SpeakerAvatar.supportedAvatars(chatSpeakerIds).slice(0, MAX_SPEAKER_AVATARS);
  const hasCut = availableSpeakers.length < chatSpeakerIds.length;
  // const prettifyWatchers = (count) => count > 0 ? count + ' people watching ' : '';
  return (
    <ListItem button component={RouterLink} to={'/chat/' + chatId} className={classes.chatListItems}>
      <ListItemAvatar>
        <ChatIconAvatar chatSnapshot={chatSnapshot}/>
      </ListItemAvatar>
      <ListItemText
        secondaryTypographyProps={{component: 'span'}}
        primary={
          <Typography>
            {chatSnapshot.topic || '???'}{/*<LockIcon color="disabled" fontSize="small"/>*/}
          </Typography>
        }
        secondary={
          <ListItem disableGutters className={classes.speakerAvatarContainer}>
            {availableSpeakers.map(speakerUid =>
              <SpeakerAvatar key={speakerUid} speaker={speakerUid}
                             className={classes.speakerAvatarMicro}/>)}
            {hasCut ? ' …' : ''}
          </ListItem>
        }/>
      <IconButton size="small" style={{minWidth: 0}}>  {/*onclick={e => e.preventDefault()}*/}
        {chatWatchers > 0 ? <VisibilityIcon/> : <VisibilityOffIcon color="disabled"/>}
      </IconButton>
      <Box>{chatWatchers > 0 && chatWatchers}</Box>
      {/*<ListItemSecondaryAction>*/}
      {/*  <IconButton edge="end" aria-label="..."><ThumbUpIcon color="action" fontSize="small"/></IconButton>*/}
      {/*</ListItemSecondaryAction>*/}
    </ListItem>
  );
}

function ChatsGroup({chats, title, summary, classes}: { chats: ChatData[], title: any, summary: any, classes: any }) {
  if (chats.length === 0) return <React.Fragment/>;
  return (
    <List>
      {/* Title */}
      <ListItem>
        <ListItemText
          primary={
            <Typography variant="h6" align="center" color="primary" style={{marginBottom: '12px'}}>
              {title}
            </Typography>
          }
          secondary={
            `In these ${chats.length} chats, bots talk to one another.`
          }/>
      </ListItem>

      {/* Chat List Items */}
      {chats.map((chatData, idx) => <React.Fragment key={chatData.chatId}>
        {idx > 0 && <Divider style={{backgroundColor: 'rgba(233, 30, 99, 0.1)', margin: '0 -8px'}}/>}
        <ChatListItem chatData={chatData} classes={classes}/>
      </React.Fragment>)}

      {/* Bottom Summary */}
      {summary &&
      <ListItem>
        <ListItemText secondary={summary}/>
      </ListItem>}
    </List>
  );
}


export function ViewChatsList() {
  const classes = useLocalStyles();

  // state of the component
  const [chats, setChats] = React.useState<ChatData[]>([]);
  const [stage, setStage] = React.useState<StageTypes.StageSnapshot | null>(null);
  const [hasTimeout, setHasTimeout] = React.useState(false);

  // handle mount/unmount of the component
  React.useEffect(() => {
    // timeout handling
    const timeoutDetectionIn = setTimeout(() => setHasTimeout(true), 8000);
    const stopTimeoutDetection = () => clearTimeout(timeoutDetectionIn);

    // subscribe to Conf changes
    const callbacks: ConfSubscriber = {
      onConf: (chats, stage) => {
        // note: filter to remove chatData that hasn't been detailed by the server yet (created by /chat/url)
        setChats(chats.filter(chatData => chatData.chatSnapshot));
        setStage(stage);
        if (chats.length) stopTimeoutDetection();
      }
    };
    stageClient.subscribeToConf(callbacks);

    // what do do when uninstalling the component (unregister all)
    return () => {
      stageClient.unsubscribeFromConf(callbacks);
      stopTimeoutDetection();
    }
  }, []);

  // secondary live text
  let summary = '';
  if (stage && stage.totalLinesCount) {
    const linesCount = stage.totalLinesCount;
    summary += `AI characters have generated ${linesCount.toLocaleString()} messages since May 2020,`;
    const linesScale = 563; // TODO: check
    const episodes = ~~(linesCount / linesScale);
    summary += ` Equivalent to ${episodes.toLocaleString()} TV show episodes.`;
  }
  if (stage && stage.connectionsCount) summary += ` Hello master, there are ${stage.connectionsCount.toLocaleString()} clients connected.`;

  return (<React.Fragment>
    {/* Post to explain the site */}
    <Post closePersistedKey={LocalStorageKeys.tooltip_intro}
          content={<Messaging.text.IntroComponent/>} actions={Messaging.actions.intro}/>

    <Container maxWidth="md" className={classes.centerVertical}>
      {/* Chats List */}
      <Paper elevation={8} className={classes.chatListPaper}>
        {chats.length === 0 && <List>
          {!hasTimeout && <ListItem style={{textAlign: 'center', display: 'block'}}>
            <ListItemIcon><CircularProgress/></ListItemIcon>
            <ListItemText secondary="Loading chats ..."/>
          </ListItem>}
          {hasTimeout && <ListItem>
            <ListItemIcon><BrokenImageIcon/></ListItemIcon>
            <ListItemText
              primary='There seems to be an issue with the server, and we were not able to load any chat.'
              secondary='Issue with the server connection: timeout.'/>
          </ListItem>}
        </List>}
        {chats.length > 0 &&
        <ChatsGroup chats={chats} title={Brand.chatsListTitle} summary={summary} classes={classes}/>}
      </Paper>

      {/* Topics of the Day */}
      <Paper elevation={4} className={classes.dailyPaper}>
        <DailyFeed/>
      </Paper>

    </Container>
  </React.Fragment>);
}
